import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
// import Map from '../components/Map'
import { lazy } from 'react';
import useDebouncedCallback from '../hooks/useDebouncedCallback'
import useThrottledValue from '../hooks/useThrottledValue'
import TimeScrubber from '../components/scrubber/TimeScrubber'
import { SensorLegendTable } from '../components/scrubber/SensorDataGrid'
import { Box, CircularProgress, IconButton, Tooltip, Typography, Button, Stack, Alert, Paper } from '@mui/material'
import Legend from '../components/maps/Legend'
// import AppBarSearch from '../components/AppBarSearch';
import AppBar from '../components/menu/AppBar'
// import BottomDrawer from '../components/BottomDrawer';
import { useClosestTime } from '../utils/ogc'
import { SelectedPopup } from '../components/maps/MapPopup'
import type { GeoJsonFeature } from '../utils/ogc'
import datemathParser, { nowWithin, nowWithinAndShortRange } from '../utils/datemath'
import { useEventDepthData } from '../layers/event_depth_data_binsearch_dictcomp'
import { useSensorLocations, type AugmentedSensorFeature } from '../layers/sensor_locations'
import { usePastFloodEvents } from '../layers/past_flood_events'
import ResponsiveDrawer from '../components/detail/SensorDetailsDrawer'
import SensorDetailContent from '../components/detail/SensorDetails';
import TimePresetMenuAndButton from '../components/menu/TimePresetMenuAndButton'
import Moon from '../components/menu/MoonWithPopup'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { WebMercatorViewport, type PickingInfo } from '@deck.gl/core';
import LogoLoader from '../components/LogoLoader';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import AllSensorsCalendarModal from '../components/calendar/AllSensorsCalendarModal';
import BrushedMiniDepthChart from '../components/charts/BrushedMiniDepthChartNew';
import RefreshToasts from '../components/menu/RefreshToasts';
import { useMaxPrecipData } from '../layers/max_precip_data';
import { CurrentLocationButton, CurrentLocationMarker, useUserLocation } from '../components/maps/CurrentLocation';
import MiniDepthChart from '../components/charts/MiniDepthChart';
import { format } from 'date-fns-tz';
import SpeedDialMenu from '../components/menu/SpeedDialMenu';
import { LIVE_PRESET_RANGES, PRESET_RANGES } from '../config';
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { usePastFloodEventContours } from '../layers/past_flood_event_contours_backup_cpu';
import useData from '../hooks/useData';
import WelcomeDialog, { WelcomeDialogAndButton } from '../components/menu/WelcomeDialog';
import logoUrl from '../assets/floodnet_logo_bw_5in.png';
import { formatDuration, formatRelative, intervalToDuration, set } from 'date-fns';
import useTimeRange from '../hooks/useTimeRange'
import ResetMapViewButton from '../components/maps/ResetMapViewButton';
import SensorListModal from '../components/list/SensorListModal';
import { Menu, MenuItem, ListSubheader, Divider, ListItemIcon, ListItemText, useMediaQuery } from '@mui/material';
import { useInitFeatures } from '../components/maps/BoundsListener';

const Map = lazy(() => import('../components/maps/Map'));


const DEFAULT_VIEW = {
  // 40.665765%2C-73.968166%2C11.35%2C0%2C0
  latitude: 40.665765,
  longitude: -73.968166,
  zoom: 11,
  bearing: 0,
  pitch: 0,
}


const useStaticAugmentedSensorFeatures = (sensorFeatures: GeoJsonFeature[] | null | undefined, depthFeatures: GeoJsonFeature[] | null | undefined, allPastEventFeatures: GeoJsonFeature[] | null | undefined, timeRange: [Date, Date]) => {
  return React.useMemo(() => {
    if (!sensorFeatures || !depthFeatures || !allPastEventFeatures) return [];
    const out: Record<string, AugmentedSensorFeature> = {};
    for (const f of sensorFeatures) {
      out[f.properties.id as string] = {
        ...f,
        properties: {
          ...f.properties,
          max_depth_inches: 0,
          current_depth_inches: undefined,
          time: [],
          labels: [],
          depth_inches: [],
          past_events: [],
          events: [],
          max_event: null,
        }
      };
    }

    for (const f of depthFeatures.toSorted((a, b) => (a.properties.time.getTime() - b.properties.time.getTime()))) {
      const fid = f.properties.deployment_id;
      const fout = out[fid];
      if (!fout) continue;
      fout.properties.max_depth_inches = Math.max(fout.properties.max_depth_inches, f.properties.depth_inches);
      fout.properties.depth_inches.push(f.properties.depth_inches);
      fout.properties.time.push(f.properties.time);
      fout.properties.labels.push(f.properties.label);
    }
    for (const f of allPastEventFeatures.toSorted((a, b) => (a.properties.start_time.getTime() - b.properties.start_time.getTime()))) {
      // console.log('Associating past event to sensor:', f);
      const fid = f.properties.deployment_id;
      const fout = out[fid];
      if (!fout) continue;
      fout.properties.past_events.push(f);
      const t0 = f.properties.start_time;
      const t1 = f.properties.end_time;
      if ((t0 <= timeRange[1]) && ((t1 || new Date()) >= timeRange[0])) {
        fout.properties.events.push(f);
        if (!fout.properties.max_event || (f.properties.max_depth_inches > fout.properties.max_event.properties.max_depth_inches)) {
          fout.properties.max_event = f;
        }
      }
    }
    // console.log('Building static augmented sensor features');
    // console.log('Augmented sensor features:', out);
    return Object.values(out) as AugmentedSensorFeature[];
  }, [sensorFeatures, depthFeatures, allPastEventFeatures, timeRange]);
}

const useMapFilteredSensorFeatures = (features: AugmentedSensorFeature[] | undefined, mapBounds: {south:number;west:number;north:number;east:number} | null) => {
  const miniFeatures = React.useMemo(() => {
    if (!mapBounds || !features) return features
    const { south, west, north, east } = mapBounds
    return (features || []).filter((f: GeoJsonFeature) => {
      const c = f?.geometry?.coordinates
      if (!Array.isArray(c) || c.length < 2) return false
      const lng = c[0], lat = c[1]
      return lat >= south && lat <= north && lng >= west && lng <= east
    })
  }, [features, mapBounds])
  return miniFeatures;
}

const useTimeRangeFilteredSensorFeatures = (features: AugmentedSensorFeature[] | undefined, timeRange: [Date, Date] | [Date, Date, boolean]) => {
  return React.useMemo(() => {
    if (!features) return features;
    const [from, to] = timeRange;
    return features.filter((f) => {
      const times = (f?.properties?.time as Date[]) || [];
      if (!times.length) return false;
      if (times[0] > to || times[times.length - 1] < from) return false;
      return times.some((t) => t >= from && t <= to);
    });
  }, [features, timeRange]);
};

const useCurrentTimeSensorFeatures = (features: AugmentedSensorFeature[] | undefined, depthFeatures: GeoJsonFeature[] | null | undefined, currentTime: Date | undefined) => {
  const closestTime = useClosestTime(depthFeatures, currentTime);
  return React.useMemo(() => {
    if (!features || !currentTime) return features;
    const out: Record<string, AugmentedSensorFeature> = {};
    // narrow/assert the type so we can index by string key safely
    const ct = (closestTime || {}) as Record<string, { feature: GeoJsonFeature }>;
    for (const f of features) {
      const k = f.properties.id as string;
      const ctk = ct[k];
      out[k] = {
        ...f,
        properties: {
          ...f.properties,
          current_depth_inches: (ctk ? ctk.feature?.properties?.depth_inches : undefined) as number | undefined,
        }
      };
    }
    return Object.values(out) as AugmentedSensorFeature[];
  }, [features, currentTime, closestTime]);
}

const useSelectedFeature = (staticAugSensorFeatures, search) => {
  const router = useRouter();


  // const [hoverInfo, setHoverInfo] = React.useState<PickingInfo<GeoJsonFeature> | null>(null)
  const [selectedFeatureKey, setSelectedFeatureKey] = React.useState<string | null>(() => {
    const k = search.k;
    if (k) {
      try { return decodeURIComponent(k) } catch { return k }
    }
    return null;
  });
  // keep router search in sync when selection changes
  React.useEffect(() => {
    router.navigate({
      to: '.',
      replace: true,
      search: (prev: any) => ({
        ...prev,
        k: selectedFeatureKey || undefined,
      }),
    });
  }, [router, selectedFeatureKey]);


  
  const selectedFeatures = React.useMemo(() => {
    if (!selectedFeatureKey || !staticAugSensorFeatures) return null;
    const f = staticAugSensorFeatures.filter(f => {
      const p = f?.properties;
      const key = p?.deployment_id || p?.id || null;
      return key === selectedFeatureKey;
    }) || null;
    return f.map(f => ({ object: f }));
  }, [selectedFeatureKey, staticAugSensorFeatures]);
  const selectedFeature = selectedFeatures?.[0] || null;
  // useEffect(() => { console.log('Selected feature changed:', selectedFeature); }, [selectedFeature]);

  const setSelectedFeature = useCallback((f: { object: GeoJsonFeature } | null) => {
    // console.log('Setting selected feature:', f);
    const p = f?.object?.properties;
    const key = p?.deployment_id || p?.id || null;
    // console.log('Setting selected feature key:', key, p);
    setSelectedFeatureKey(key);
  }, [setSelectedFeatureKey]);
  const setSelectedFeatureByGeojson = useCallback((f: GeoJsonFeature | null) => {
    return setSelectedFeature(f ? { object: f } : null);
  }, [setSelectedFeature]);

  const toggleSelectedFeature = useCallback((f: GeoJsonFeature) => {
    const p = f?.properties;
    const key = p?.deployment_id || p?.id || null;
    console.log('Setting selected feature key:', key, p);
    setSelectedFeatureKey((prev) => prev === key ? null : key);
  }, [setSelectedFeatureKey]);
  const deselectFeature = useCallback(() => setSelectedFeature(null), [setSelectedFeature]);
  // console.log('Selected feature key:', selectedFeatureKey);

  return { selectedFeature, selectedFeatureKey, selectedFeatures, setSelectedFeature, setSelectedFeatureByGeojson, toggleSelectedFeature, deselectFeature };
}


export default function App({ mode, search: searchParams }: { mode?: string; search?: { mode?: string; v?: string; k?: string } }) {
  const router = useRouter();
  const search = searchParams || { mode: undefined, v: undefined, k: undefined };
  const initialView = React.useMemo(() => {
    const v = search.v;
    if (!v) return null;
    const [lat, lng, zoom, heading, tilt] = v.split('_').map(Number);
    return { latitude: lat, longitude: lng, zoom: zoom, bearing: heading, pitch: tilt };
  }, [search?.v]);

  

  const [sensorListOpen, setSensorListOpen] = React.useState(false)
  const [calendarOpen, setCalendarOpen] = React.useState(false)
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuAnchor)
  const closeMenu = () => setMenuAnchor(null)
  const visMode = mode || search?.mode || 'simple';
  const visModes = useMemo(() => visMode.split(','), [visMode]);
  const simpleMode = visModes.includes('simple');
  const contourMode = visModes.includes('contours');
  // const simpleMode = visMode === 'simple';
  // const contourMode = visMode === 'contours';

  

  const { 
    timeRange, 
    setTimeRange, 
    currentTime, 
    setCurrentTime, 
    throttledCurrentTime, 
    currentDateStartOfDay, 
    throttledCurrentDateStartOfDay 
  } = useTimeRange(search);
  const [playing, setPlaying] = React.useState(false);

  const legendMode = currentTime ? 'overTime' : 'max'
  // const [legendMode, setLegendMode] = React.useState<LegendMode>('overTime')
  const setLegendMode = useCallback((mode: 'max' | 'overTime') => {
    if (mode === 'max') {
      setCurrentTime(undefined)
    } else {
      // Set current time to middle of time range
      setCurrentTime(timeRange[0])
    }
  }, [timeRange, setCurrentTime]);
  
  const [pastEventTimeRange, setPastEventTimeRange] = useState<[Date, Date, boolean]>(() => [new Date('2020-10-01'), new Date("2026-01-01"), false]);
  
  /* -------------------------------------------------------------------------- */
  /*                                    Data                                    */
  /* -------------------------------------------------------------------------- */

  const { features: sensorFeatures, loading: sensorLoading } = useSensorLocations();

  const isSmall = useMediaQuery('(max-width: 900px)');

  // const { features: miniFeaturesRaw } = useEventWithDepthData(fromTime, toTime);
  const [mapBounds, setMapBounds] = React.useState<{south:number;west:number;north:number;east:number} | null>(() => {
    // const b = initialHashParams.get('b')
    // if (b) {
    //   const [south, west, north, east] = b.split(',').map(Number)
    //   if ([south, west, north, east].every(v => Number.isFinite(v))) return { south, west, north, east }
    // }
    // const v = search.v;
    // if (v) {
    //   const [lat, lng, zoom, heading, tilt] = v.split(',').map(Number);
    //   // calculate bounds from center/zoom using viewport object
    //   const viewport = new WebMercatorViewport({ width: 800, height: 600, latitude: lat, longitude: lng, zoom, bearing: heading, pitch: tilt });
    //   const bounds = viewport.getBounds();
    //   return { south: bounds[1], west: bounds[0], north: bounds[3], east: bounds[2] };
    // }
    return null;
  });
const initBounds = useInitFeatures({
   features: sensorFeatures,
   padding: isSmall
      ? { top: 32, bottom: 280, left: 32, right: 32 }
      : { top: 32, bottom: 260, left: 48, right: 320 },
});
  const [initialViewState, setInitialViewState] = React.useState(initialView || initBounds || DEFAULT_VIEW);
  React.useEffect(() => {
    if (!initialView && initBounds && !initialViewState) {
      setInitialViewState(initBounds);
    }
  }, [initialView, initBounds, initialViewState]);
  const resetMapView = useCallback(() => {
    setInitialViewState({ ...DEFAULT_VIEW, pitch: Math.random() * 0.1 + 0.1});
    console.log('Resetting map view to initial view:', DEFAULT_VIEW);
  }, []);

  // const { features: eventFeatures } = useEventWithDepthData(timeRange[0], timeRange[1]);
  const { features: depthFeatures, loading: depthLoading } = useEventDepthData(timeRange);
  const { features: allPastEventFeatures, loading: pastEventsLoading } = usePastFloodEvents(pastEventTimeRange);
  const { features: maxPrecipFeatures, loading: precipLoading } = useMaxPrecipData(timeRange[0], timeRange[1]);
  const { features: contourFeatures } = usePastFloodEventContours(contourMode ? timeRange : undefined);
  // const { features: allPastEventFeatures, loading: pastEventsLoading } = { features: [], loading: false }; // disable past events for now
  const pastEventFeatures = React.useMemo(() => {
    if (!allPastEventFeatures) return allPastEventFeatures;
    return allPastEventFeatures.filter(f => {
      const t0 = f.properties.start_time;
      const t1 = f.properties.end_time;
      return (t0 <= timeRange[1]) && ((t1 || new Date()) >= timeRange[0]);
    });
  }, [allPastEventFeatures, timeRange]);

  const staticAugSensorFeatures = useStaticAugmentedSensorFeatures(sensorFeatures, depthFeatures, allPastEventFeatures, timeRange);
  const { selectedFeature, selectedFeatureKey, selectedFeatures, setSelectedFeature, setSelectedFeatureByGeojson, toggleSelectedFeature, deselectFeature } = useSelectedFeature(staticAugSensorFeatures, search);
  const mapFilteredFeatures = useMapFilteredSensorFeatures(staticAugSensorFeatures, mapBounds);
  const augSensorFeatures = useCurrentTimeSensorFeatures(mapFilteredFeatures, depthFeatures, currentTime);
  const timeFilteredFeatures = useTimeRangeFilteredSensorFeatures(mapFilteredFeatures, timeRange);
  const staticFeaturesWithDepth = useTimeRangeFilteredSensorFeatures(staticAugSensorFeatures, timeRange);
  // console.log({
  //   augSensorFeatures,
  //   staticAugSensorFeatures,
  //   // mapFilteredFeatures,
  //   miniFeatures: mapFilteredFeatures,
  // });

  const { requestUserLocation, userLocation, error: locError } = useUserLocation({
    setViewState: setInitialViewState,
  });

  return (
    <Box display="flex" flexDirection="column" height="100dvh" width="100vw" sx={{ overflow: "hidden" }}>
      <AppBar goToHome={resetMapView}>
     
        <Spacer />

        <CurrentTimeRangeLabel timeRange={timeRange} />

        <Spacer />

           
        <Moon currentTime={currentDateStartOfDay || timeRange[0]} size={24} />
        {/* {simpleMode ? null : currentDateStartOfDay ? <Moon currentTime={currentDateStartOfDay} size={24} /> : null} */}

        <IconButton color="default" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon />
        </IconButton>
      </AppBar>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        keepMounted
        MenuListProps={{ dense: true, sx: { minWidth: 220, py: 0 } }}
      >
        <ListSubheader disableSticky sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.4, px: 2, py: 0.75 }}>
          Map actions
        </ListSubheader>
        <MenuItem
          dense
          onClick={() => {
            closeMenu();
            resetMapView();
          }}
        >
          <ListItemText primary="Reset Map" secondary="Zoom out to show whole map" />
        </MenuItem>
        {requestUserLocation && <MenuItem
          dense
          onClick={() => {
            closeMenu();
            requestUserLocation?.();
          }}
        >
          <ListItemText primary="My Location" secondary="Show your current location on the map" />
        </MenuItem>}

        {!simpleMode && (<>
        
        <Divider />
        <ListSubheader disableSticky sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.4, px: 2, py: 0.75 }}>
          Tools
        </ListSubheader>
        <MenuItem
          dense
          onClick={() => {
            closeMenu();
            setCalendarOpen(true);
          }}
        >
          <ListItemText primary="Calendar" secondary="Past flood events" />
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            closeMenu();
            setSensorListOpen(true);
          }}
        >
          <ListItemText primary="Search" secondary="Browse and select sensors" />
        </MenuItem>

        <MenuItem
          dense
          onClick={() => {
            closeMenu();
            router.navigate({ to: '/', search: (prev: any) => ({
              ...prev,
              mode: simpleMode ? 'detailed' : 'simple',
            }) });
          }}
        >
          <ListItemText primary="Simplified Interface" secondary="Simplify the interface for event sharing" />
        </MenuItem>
        
        </>).props.children}

        {/* <Divider />
        <ListSubheader disableSticky sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.4, px: 2, py: 0.75 }}>
          Help
        </ListSubheader>
        <MenuItem onClick={closeMenu} dense disableRipple>
          <ListItemText primary={} />
        </MenuItem> */}
      </Menu>
      <AllSensorsCalendarModal
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        events={allPastEventFeatures || []}
        timeRange={timeRange}
        fromTime={pastEventTimeRange[0]}
        toTime={pastEventTimeRange[1]}
        onSelectRange={setTimeRange}
      />
      <SensorListModal
        open={sensorListOpen}
        onClose={() => setSensorListOpen(false)}
        features={staticAugSensorFeatures}
        currentTime={throttledCurrentDateStartOfDay || undefined}
        onSelect={setSelectedFeatureByGeojson}
      />
      <Box position="relative" flexGrow={1} sx={{ overflow: "hidden" }}>
        <Suspense fallback={
          // <LogoLoader fullscreen size={104} label="Loading map…" />
          <CircularProgress size={48} thickness={5} sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          }>
        <Map 
          // key={mapKey}
          // defaultBounds={bounds}
          currentTime={currentTime}
          timeRange={timeRange} 
          setSelectedFeature={setSelectedFeature} 
          // onHover={(info: PickingInfo) => {
          //   setHoverInfo(info && info.object ? info : null)
          // }}
          // hoveredKey={highlightedKey}
          sensorFeatures={sensorFeatures}
          contourFeatures={contourFeatures}
          offlineHidden={simpleMode}
          staticAugSensorFeatures={staticAugSensorFeatures}
          depthFeatures={depthFeatures}
          pastEventFeatures={pastEventFeatures}
          selectedFeature={selectedFeature?.object as GeoJsonFeature | null}
          onBoundsChange={setMapBounds}
          initialView={initialViewState}
        >
          {selectedFeature && 
          <AdvancedMarker position={{ lat: selectedFeature.object.geometry.coordinates[1], lng: selectedFeature.object.geometry.coordinates[0] }} zIndex={101}>
            <SelectedPopup
              info={selectedFeature}
              features={staticAugSensorFeatures}
              onClose={deselectFeature}
              timeRange={timeRange}
              currentTime={currentTime}
              detailed
            />
          </AdvancedMarker>
          }
          <CurrentLocationMarker userLocation={userLocation} />
        </Map>
        </Suspense>
      </Box>
          <Box position="absolute" top={{ xs: 50, md: 8+48 }} left={{ xs: 2, md: 8 }} zIndex={10}>
            <Legend legendMode={legendMode} setLegendMode={setLegendMode} simpleMode={simpleMode} />
          </Box>
        <TimeScrubber
          timeRange={timeRange}
          currentTime={currentTime}
          onTimeChange={setCurrentTime}
          onTimeRangeChange={simpleMode ? undefined : setTimeRange}
          playing={playing}
          setPlaying={setPlaying}
          chart={({ expanded }) => (
            !currentTime ? null :
            (
            simpleMode ?
            <MiniDepthChart
              timeRange={timeRange} 
              currentTime={currentTime}
              // playing={playing}
              height={expanded ? 200 : 50} 
              precip={maxPrecipFeatures ?? []}
              features={mapFilteredFeatures ?? []} 
              loading={depthLoading}
              highlightedKey={selectedFeatureKey} 
              onClick={toggleSelectedFeature}
              caption={mapBounds ? `${timeFilteredFeatures?.length ?? 0}/${staticFeaturesWithDepth?.length ?? 0} floods in map view` : undefined}
            />
            :
            <BrushedMiniDepthChart
              timeRange={timeRange}
              height={expanded ? 200 : 50} 
              currentTime={currentTime}
              playing={playing}
              precip={maxPrecipFeatures ?? []}
              features={mapFilteredFeatures ?? []}
              loading={depthLoading}
              highlightedKey={selectedFeatureKey} 
              onClick={toggleSelectedFeature}
              onTimeRangeChange={setTimeRange}
              caption={mapBounds && staticFeaturesWithDepth?.length ? `${timeFilteredFeatures?.length ?? 0}/${staticFeaturesWithDepth?.length ?? 0} floods in map view` : undefined}
            />
            )
          )}
        >
          <SensorLegendTable 
            currentTime={currentTime} 
            features={augSensorFeatures} 
            highlightedKey={selectedFeatureKey} 
            onSelect={toggleSelectedFeature} 
          />
        </TimeScrubber>
        
        {simpleMode ? null : <SensorDetailsDrawer
          info={selectedFeature}
          onClose={deselectFeature}
          timeRange={timeRange}
          currentTime={selectedFeature ? currentTime : undefined}
          onSelectRange={setTimeRange}
          setInitialViewState={setInitialViewState}
        />}

        <RefreshToasts items={[
          { key: 'sensors', label: 'Loading sensors…', open: !!sensorLoading },
          { key: 'depth', label: 'Loading depth over time…', open: !!depthLoading },
          { key: 'past', label: 'Loading historic events…', open: !!pastEventsLoading },
          { key: 'precip', label: 'Loading precipitation data…', open: !!precipLoading },
          { key: 'loc', label: locError || '', open: !!locError, severity: 'error', maxDuration: 6000 },
        ]} />
    </Box>
  )
}

const Spacer = React.memo(() => {
    return (
        <Box flexGrow={1} />
    );
});


const CurrentTimeRangeLabel = React.memo(({ timeRange }: { currentTime?: Date; timeRange: [Date, Date] }) => {
  const from = timeRange[0];
  const to = timeRange[1];
  const fromStr = useMemo(() => format(from, 'PP p'), [from]);
  const toStr = useMemo(() => format(to, 'PP p'), [to]);
  const durationNatural = useMemo(() => from && to ? formatDuration(intervalToDuration({ start: from, end: to })) : '', [from, to]);
  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant="body2" color="inherit" textAlign="center" fontSize={{ xs: '0.9rem', sm: '1.2em' }} sx={{ fontWeight: 'bold' }}>
        Flood Summary
      </Typography>
      <Paper elevation={4} sx={{
        position: 'absolute', 
        top: '100%', 
        left: '50%', 
        transform: 'translateX(-50%)', 
        whiteSpace: 'nowrap',
        mt: 0.2,
        maxWidth: '100vw',
        width: 'max-content',
        background: 'background.paper',
        mt: 0.2,
        px: 1.5,
        borderRadius: 2,
      }}>

        <Tooltip title={`Displaying data spanning ${durationNatural}`} arrow>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.1} sx={{ flexGrow: 0 }} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Typography variant="body2" color="inherit" textAlign="justify" fontSize={{ xs: '0.7rem', sm: 'inherit' }} sx={{ fontWeight: 'bold' }}>
          {fromStr}
        </Typography>
        <Typography variant="body2" color="inherit" sx={{ lineHeight: 0 }}>
          &nbsp;–&nbsp;
        </Typography>
        <Typography variant="body2" color="inherit" textAlign="justify" fontSize={{ xs: '0.7rem', sm: 'inherit' }} sx={{ fontWeight: 'bold' }}>
          {toStr}
        </Typography>
        </Box>
        </Tooltip>
      </Paper>
    </Box>

  );
});


const SensorDetailsDrawer = React.memo(({ info, onClose, ...props }: { info: PickingInfo<GeoJsonFeature>; onClose: () => void; }) => {
  return (
    <ResponsiveDrawer open={!!info} onClose={onClose}>
      {info ? <SensorDetailContent info={info} onClose={onClose} {...props} /> : null}
    </ResponsiveDrawer>
  );
});
